import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getAttribution, track } from '../utils/analytics';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // Don't block if no token - pages can render immediately
  const storedToken = localStorage.getItem('token');
  const [loading, setLoading] = useState(!!storedToken);
  const [authChecked, setAuthChecked] = useState(false);
  const [token, setToken] = useState(storedToken);

  const checkAuth = useCallback(async () => {
    // Always ask the server who we are. This detects BOTH artist/admin tokens
    // (localStorage Bearer) AND customer cookie sessions (httpOnly session_token).
    try {
      const stored = localStorage.getItem('token');
      const headers = stored ? { Authorization: `Bearer ${stored}` } : {};
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers,
        withCredentials: true,
        timeout: 6000
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
      if (localStorage.getItem('token')) {
        setToken(null);
        localStorage.removeItem('token');
      }
    } finally {
      setLoading(false);
      setAuthChecked(true);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const verifyAdmin2FA = async (email, otpCode) => {
    const response = await axios.post(`${API_URL}/api/auth/admin-2fa-verify`, 
      { email, otp_code: otpCode }, { withCredentials: true }
    );
    const newToken = response.data.token || response.data.access_token;
    setToken(newToken);
    localStorage.setItem('token', newToken);
    setUser(response.data.user);
    return response.data;
  };

  // Ends the session on this device only (server revokes just this session).
  const logout = async () => {
    const role = user?.role;
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      // Ignore errors
    }
    track('logout', { type: role === 'customer' ? 'host' : role === 'artist' ? 'artist' : 'team' });
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
  };

  const otpSend = async (phone) => {
    const res = await axios.post(`${API_URL}/api/otp/send`, { phone }, { withCredentials: true });
    return res.data;
  };

  const otpVerify = async (phone, code) => {
    const res = await axios.post(`${API_URL}/api/otp/verify`, { phone, code, attribution: getAttribution() }, { withCredentials: true });
    setUser(res.data.user);
    return res.data;
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      authChecked,
      token,
      logout,
      checkAuth,
      verifyAdmin2FA,
      otpSend,
      otpVerify,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
